import { useEffect, useState } from "react";
import { systemApi } from "../api/services";
import { useAuth } from "../App";
import { T } from "../tokens";

/**
 * Polls /system/maintenance every minute and shows a sticky banner when the
 * platform is in maintenance mode. Hidden for admins (who can already
 * navigate freely). Sits above ImpersonationBanner / UpdatePrompt because
 * it's the most surprising thing to discover mid-flow.
 */
export default function MaintenanceBanner() {
  const { user } = useAuth();
  const [state, setState] = useState({ on: false, message: "" });

  useEffect(() => {
    let mounted = true;
    let interval;

    const tick = async () => {
      try {
        const res = await systemApi.maintenance();
        if (!mounted) return;
        setState({
          on:      Boolean(res.data?.maintenance),
          message: res.data?.message || "",
        });
      } catch {
        // Network blips shouldn't show or hide the banner — keep last known.
      }
    };

    tick();
    interval = setInterval(tick, 60_000);
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  if (!state.on) return null;
  // Admins see a different banner reminding them maintenance is on (so they
  // remember to flip it back), but customers shouldn't see the admin reminder.
  const isAdmin = user?.role === "admin";

  return (
    <div role="status" style={{
      position: "sticky", top: 0, zIndex: 199,
      background: isAdmin
        ? "linear-gradient(90deg, #2a1a08, #3a2410, #2a1a08)"
        : "linear-gradient(90deg, #2a1c08, #1a1208, #2a1c08)",
      borderBottom: `1px solid ${isAdmin ? "#facc15" : T.gold}`,
      color: isAdmin ? "#facc15" : T.goldLt,
      padding: "9px 24px",
      textAlign: "center", fontSize: 13, fontFamily: "'DM Sans',sans-serif",
      letterSpacing: ".02em",
    }}>
      <span style={{ marginRight: 8 }}>🚧</span>
      {isAdmin ? (
        <>
          <strong>Maintenance mode is ON.</strong> Customers see "{state.message}". Visit{" "}
          <a href="/admin/settings" style={{ color: "inherit", textDecoration: "underline" }}>
            Settings
          </a>
          {" "}to disable.
        </>
      ) : (
        <>{state.message || "Fayriza is undergoing scheduled maintenance. We will be back shortly."}</>
      )}
    </div>
  );
}
